import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, ShieldX, BellRing } from "lucide-react";
import { api } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { PageHeader, EmptyState, shortDate } from "../components/common";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";

const CFG = {
  active: { icon: ShieldCheck, color: "text-success", bar: "bg-success", label: "Active" },
  expiring: { icon: ShieldAlert, color: "text-warning", bar: "bg-warning", label: "Expiring soon" },
  expired: { icon: ShieldX, color: "text-destructive", bar: "bg-destructive", label: "Expired" },
};

export default function Warranties() {
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["warranties"],
    queryFn: async () => (await api.get("/warranties")).data,
  });

  const reminders = items.filter((w) => w.status === "expiring");

  return (
    <AppLayout>
      <PageHeader eyebrow="Coverage" title="Warranty tracking" subtitle="Keep an eye on every warranty and get a heads-up before cover ends." />

      {reminders.length > 0 && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4" data-testid="reminders-banner">
          <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
          <div>
            <p className="font-bold text-warning">{reminders.length} warranty{reminders.length > 1 ? "ies" : ""} expiring soon</p>
            <p className="text-sm text-warning/90">{reminders.map((r) => r.item_summary).join(", ")} — act before cover ends.</p>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 w-full rounded-xl" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="No warranties tracked yet" subtitle="Claim receipts that include a warranty and they'll appear here." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {items.map((w) => {
            const cfg = CFG[w.status] || CFG.active;
            const totalDays = (w.warranty_months || 12) * 30;
            const pct = Math.max(0, Math.min(100, (w.days_left / totalDays) * 100));
            const Icon = cfg.icon;
            return (
              <Link key={w.receipt_id} to={`/wallet/receipt/${w.receipt_id}`} data-testid={`warranty-${w.receipt_id}`}
                className="rounded-xl border border-border bg-card p-5 transition-shadow hover:shadow-md">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{w.store_name}</p>
                    <p className="mt-1 font-heading font-bold">{w.item_summary}</p>
                  </div>
                  <Icon className={`h-6 w-6 ${cfg.color}`} />
                </div>
                <div className="mt-4">
                  <div className="mb-1.5 flex justify-between text-xs">
                    <span className={`font-bold ${cfg.color}`}>{cfg.label}</span>
                    <span className="text-muted-foreground">{w.days_left >= 0 ? `${w.days_left} days left` : `expired ${-w.days_left} days ago`}</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div className={`h-full rounded-full ${cfg.bar}`} style={{ width: `${w.status === "expired" ? 100 : pct}%` }} />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">Covered until {shortDate(w.warranty_expiry)}</p>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}
